import {
  CheckCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  PictureOutlined,
  PlusOutlined,
  SearchOutlined,
  StopOutlined,
} from '@ant-design/icons'
import { Alert, Button, Dropdown, Input, Popconfirm, Select, Space, Table, Tooltip, Typography, message } from 'antd'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  bulkActionSiteEntries,
  createEntryPreviewToken,
  deleteSiteEntry,
  listSiteTaxonomies,
  listTaxonomyTerms,
  searchSiteEntries,
  siteAssetVariantUrl,
  updateSiteEntry,
} from '../../api'
import type { EntryBulkAction, SaasApiError } from '../../api'
import type { ContentEntry, TaxonomyTerm } from '../../types'
import '../../ui/tenant-items.css'

const { Text } = Typography

const STATUS_LABEL: Record<string, string> = { draft: '草稿', published: '已发布', archived: '已归档' }
const VISIBILITY_LABEL: Record<string, string> = { public: '展示', unlisted: '隐藏（不索引）', private: '隐藏' }
const IMAGE_STATUS_LABEL: Record<string, string> = { ready: '已处理', processing: '处理中', failed: '异常', none: '未上传' }

function statusBadgeClass(status: string): string {
  if (status === 'published') return 'tc-badge tc-badge-success'
  if (status === 'archived') return 'tc-badge tc-badge-error'
  return 'tc-badge tc-badge-neutral'
}

function visibilityBadgeClass(visibility: string): string {
  if (visibility === 'public') return 'tc-badge tc-badge-success'
  if (visibility === 'unlisted') return 'tc-badge tc-badge-warning'
  return 'tc-badge tc-badge-neutral'
}

function formatUpdatedParts(iso: string): { date: string; time: string } {
  const d = new Date(iso)
  const date = d.toLocaleDateString('zh-CN')
  const time = d.toLocaleTimeString('zh-CN', { hour12: false })
  return { date, time }
}

/**
 * P0-C — list thumbnail with real loading/failed states (spec §14). The
 * `<img>` starts hidden behind a skeleton; `onLoad` reveals it, `onError`
 * (a genuinely broken/undecodable asset — DeepZoom readiness plays no
 * part in this, see `ensure_thumbnail_variant`'s backend contract) swaps
 * to a distinct failed-placeholder instead of the browser's native
 * broken-image icon.
 */
function ItemThumb({ workspaceId, entry }: { workspaceId: string; entry: ContentEntry }) {
  const [state, setState] = useState<'loading' | 'ready' | 'failed'>('loading')
  const src = entry.cover_asset_id ? siteAssetVariantUrl(workspaceId, entry.cover_asset_id, 'thumbnail') : null

  useEffect(() => setState('loading'), [src])

  if (!src) {
    return (
      <div className="tc-thumb-empty">
        <PictureOutlined />
      </div>
    )
  }
  return (
    <>
      {state === 'loading' && <div className="tc-thumb-loading" aria-hidden="true" />}
      {state === 'failed' && (
        <div className="tc-thumb-failed">
          <PictureOutlined />
        </div>
      )}
      <img
        src={src}
        alt={entry.label}
        className="tc-thumb"
        style={state === 'ready' ? undefined : { display: 'none' }}
        onLoad={() => setState('ready')}
        onError={() => setState('failed')}
      />
    </>
  )
}

/**
 * LiangCollection Full Website Absorption (v2) — 条目管理 / 条目列表.
 * Tenant Console UI V1 — presentation restructure only; filters, actions,
 * and data loading unchanged.
 */
export default function ItemsTab({
  workspaceId,
  siteId,
  workspaceSlug,
  siteSlug,
  siteBase,
}: {
  workspaceId: string
  siteId: string
  workspaceSlug?: string
  siteSlug?: string
  siteBase: string
}) {
  const navigate = useNavigate()
  // P0-B: Table's `sticky` offsetHeader must clear both the global shell
  // header (`--header-height`, handled by `.tc-sticky-toolbar`'s own
  // `top`) AND this page's own sticky filter toolbar — measured live
  // (search/select rows can wrap to 2 lines on narrower desktop widths)
  // rather than a guessed constant.
  const toolbarRef = useRef<HTMLDivElement | null>(null)
  const [tableStickyOffset, setTableStickyOffset] = useState(112)
  useEffect(() => {
    const el = toolbarRef.current
    if (!el) return
    const update = () => setTableStickyOffset(64 + el.getBoundingClientRect().height)
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    window.addEventListener('resize', update)
    return () => {
      ro.disconnect()
      window.removeEventListener('resize', update)
    }
  }, [])
  const [rows, setRows] = useState<ContentEntry[] | null>(null)
  const [total, setTotal] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  // -------------------------------------------------------------- Filters
  const [search, setSearch] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [categoryId, setCategoryId] = useState<string | undefined>(undefined)
  const [topicIds, setTopicIds] = useState<string[]>([])
  const [status, setStatus] = useState<string | undefined>(undefined)
  const [visibility, setVisibility] = useState<string | undefined>(undefined)
  const [imageStatus, setImageStatus] = useState<string | undefined>(undefined)
  const [sort, setSort] = useState('updated_desc')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [selectedIds, setSelectedIds] = useState<string[]>([])

  const [categoryTerms, setCategoryTerms] = useState<TaxonomyTerm[]>([])
  const [topicTerms, setTopicTerms] = useState<TaxonomyTerm[]>([])

  useEffect(() => {
    listSiteTaxonomies(workspaceId, siteId).then((r) => {
      const category = r.data.find((t) => t.key === 'category')
      const topic = r.data.find((t) => t.key === 'topic')
      if (category) listTaxonomyTerms(workspaceId, siteId, category.id).then((tr) => setCategoryTerms(tr.data))
      if (topic) listTaxonomyTerms(workspaceId, siteId, topic.id).then((tr) => setTopicTerms(tr.data))
    })
  }, [workspaceId, siteId])

  const load = useCallback(() => {
    setLoading(true)
    setError(null)
    const termIds = [...(categoryId ? [categoryId] : []), ...topicIds]
    searchSiteEntries(workspaceId, siteId, {
      content_type: 'item',
      search: search || undefined,
      taxonomy_term_ids: termIds.length ? termIds : undefined,
      status,
      visibility,
      image_status: imageStatus,
      sort,
      page,
      page_size: pageSize,
    })
      .then((r) => {
        setRows(r.data.items)
        setTotal(r.data.total)
      })
      .catch((err) => setError((err as SaasApiError).message))
      .finally(() => setLoading(false))
  }, [workspaceId, siteId, search, categoryId, topicIds, status, visibility, imageStatus, sort, page, pageSize])

  useEffect(load, [load])

  // Debounce the free-text keyword box so every keystroke doesn't fire a request.
  useEffect(() => {
    const t = setTimeout(() => {
      setPage(1)
      setSearch(searchInput)
    }, 400)
    return () => clearTimeout(t)
  }, [searchInput])

  const resetFilters = () => {
    setSearchInput('')
    setSearch('')
    setCategoryId(undefined)
    setTopicIds([])
    setStatus(undefined)
    setVisibility(undefined)
    setImageStatus(undefined)
    setSort('updated_desc')
    setPage(1)
  }

  // ---------------------------------------------------------------- Editor
  const openCreate = () => navigate(`${siteBase}/items/new`)
  const openDetail = (entry: ContentEntry) => navigate(`${siteBase}/items/${entry.id}`)
  const openEdit = (entry: ContentEntry) => navigate(`${siteBase}/items/${entry.id}/edit`)

  const onDelete = async (entry: ContentEntry) => {
    try {
      await deleteSiteEntry(workspaceId, siteId, entry.id)
      message.success(`「${entry.label}」已删除`)
      load()
    } catch (err) {
      message.error((err as SaasApiError).message)
    }
  }

  const onTogglePublish = async (entry: ContentEntry) => {
    try {
      await updateSiteEntry(workspaceId, siteId, entry.id, { status: entry.status === 'published' ? 'draft' : 'published' })
      message.success(entry.status === 'published' ? `「${entry.label}」已转为草稿` : `「${entry.label}」已发布`)
      load()
    } catch (err) {
      message.error((err as SaasApiError).message)
    }
  }

  const runBulk = async (action: EntryBulkAction) => {
    try {
      const { data } = await bulkActionSiteEntries(workspaceId, siteId, { entry_ids: selectedIds, action })
      message.success(`已对 ${data.affected} 条目执行「${bulkActionLabel(action)}」`)
      setSelectedIds([])
      load()
    } catch (err) {
      message.error((err as SaasApiError).message)
    }
  }

  const termById = useMemo(() => {
    const m = new Map<string, TaxonomyTerm>()
    for (const t of [...categoryTerms, ...topicTerms]) m.set(t.id, t)
    return m
  }, [categoryTerms, topicTerms])

  const categoryTermIds = useMemo(() => new Set(categoryTerms.map((t) => t.id)), [categoryTerms])
  const topicTermIds = useMemo(() => new Set(topicTerms.map((t) => t.id)), [topicTerms])

  const localViewUrl = (entry: ContentEntry) =>
    workspaceSlug && siteSlug ? `/site/${workspaceSlug}/${siteSlug}/items/${entry.slug}` : undefined

  // ITEM-DETAIL-PROGRESSIVE-IMAGE-P0: same "preview token for a
  // not-yet-published entry" fix as AdminItemDetailPage.tsx's
  // `openLocalView` — Item existence must never be coupled to publish
  // status, so "本地查看" must not 404 right after "保存草稿".
  const openLocalView = async (entry: ContentEntry) => {
    const url = localViewUrl(entry)
    if (!url) return
    if (entry.status === 'published') {
      window.open(url, '_blank')
      return
    }
    const win = window.open('about:blank', '_blank')
    try {
      const { data } = await createEntryPreviewToken(workspaceId, siteId, entry.id)
      const withToken = `${url}?preview_token=${encodeURIComponent(data.token)}`
      if (win) win.location.href = withToken
      else window.open(withToken, '_blank')
    } catch (err) {
      win?.close()
      message.error((err as SaasApiError).message || '生成预览链接失败')
    }
  }

  const columns = [
    {
      title: '图片',
      key: 'cover',
      width: 72,
      render: (_: unknown, entry: ContentEntry) => <ItemThumb workspaceId={workspaceId} entry={entry} />,
    },
    {
      title: '编号',
      key: 'accession_number',
      width: 110,
      render: (_: unknown, entry: ContentEntry) => <Text type="secondary">{String(entry.fields.accession_number ?? '—')}</Text>,
    },
    {
      title: '名称',
      key: 'label',
      width: 220,
      // P0-B spec §6: text-heavy column opts back to left; everything else
      // in this table defaults to centered via `.tc-data-table` CSS.
      align: 'left' as const,
      render: (_: unknown, entry: ContentEntry) => (
        <a className="tc-item-name" onClick={() => openDetail(entry)}>
          <div>{entry.label}</div>
          <span className="tc-item-slug">/{entry.slug}</span>
        </a>
      ),
    },
    {
      title: '条目分类',
      key: 'category',
      width: 140,
      render: (_: unknown, entry: ContentEntry) => {
        const cats = entry.taxonomy_term_ids.filter((id) => categoryTermIds.has(id))
        return cats.length ? (
          cats.map((id) => (
            <span key={id} className="tc-badge tc-badge-neutral">
              {termById.get(id)?.name ?? id}
            </span>
          ))
        ) : (
          <Text type="secondary">—</Text>
        )
      },
    },
    {
      title: '主题',
      key: 'topic',
      width: 160,
      render: (_: unknown, entry: ContentEntry) => {
        const topics = entry.taxonomy_term_ids.filter((id) => topicTermIds.has(id))
        return topics.length ? (
          topics.map((id) => (
            <span key={id} className="tc-badge tc-badge-info">
              {termById.get(id)?.name ?? id}
            </span>
          ))
        ) : (
          <Text type="secondary">—</Text>
        )
      },
    },
    {
      title: '作者',
      key: 'creator',
      width: 120,
      render: (_: unknown, entry: ContentEntry) => String(entry.fields.creator_display ?? entry.fields.creator ?? entry.fields.author ?? '—'),
    },
    {
      title: '年代',
      key: 'era',
      width: 100,
      render: (_: unknown, entry: ContentEntry) => String(entry.fields.creation_period_display ?? entry.fields.era ?? '—'),
    },
    {
      title: '发布状态',
      key: 'status',
      width: 90,
      render: (_: unknown, entry: ContentEntry) => (
        <span className={statusBadgeClass(entry.status)}>{STATUS_LABEL[entry.status] ?? entry.status}</span>
      ),
    },
    {
      title: '图片',
      key: 'cover_image_status',
      width: 100,
      render: (_: unknown, entry: ContentEntry) => {
        const st = entry.cover_image_status ?? (entry.cover_asset_id ? 'ready' : 'none')
        if (st === 'none') return <Text type="secondary">未上传</Text>
        if (st === 'processing') return <span className="tc-badge tc-badge-info">处理中</span>
        if (st === 'failed') return <span className="tc-badge tc-badge-error">失败</span>
        return <span className="tc-badge tc-badge-success">就绪</span>
      },
    },
    {
      title: '前端展示',
      key: 'visibility',
      width: 110,
      render: (_: unknown, entry: ContentEntry) => (
        <span className={visibilityBadgeClass(entry.visibility)}>{VISIBILITY_LABEL[entry.visibility] ?? entry.visibility}</span>
      ),
    },
    {
      title: '修改时间',
      key: 'updated_at',
      width: 150,
      render: (_: unknown, entry: ContentEntry) => {
        const { date, time } = formatUpdatedParts(entry.updated_at)
        return (
          <div className="tc-time-cell">
            <span className="tc-time-date">{date}</span>
            <span className="tc-time-clock">{time}</span>
          </div>
        )
      },
    },
    {
      title: '操作',
      key: 'actions',
      fixed: 'right' as const,
      width: 160,
      render: (_: unknown, entry: ContentEntry) => (
        <Space size={4} style={{ display: 'flex', flexWrap: 'nowrap', justifyContent: 'center', width: '100%' }}>
          <Tooltip title="编辑">
            <Button className="tc-icon-btn" size="small" icon={<EditOutlined />} onClick={() => openEdit(entry)} />
          </Tooltip>
          <Tooltip title={entry.status === 'published' ? '取消发布' : '发布'}>
            <Button
              className="tc-icon-btn"
              size="small"
              type={entry.status === 'published' ? 'default' : 'primary'}
              icon={entry.status === 'published' ? <StopOutlined /> : <CheckCircleOutlined />}
              onClick={() => onTogglePublish(entry)}
            />
          </Tooltip>
          <Tooltip title="本地查看（公开预览）">
            <Button
              className="tc-icon-btn"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => openLocalView(entry)}
              disabled={!localViewUrl(entry)}
            />
          </Tooltip>
          <Popconfirm title={`删除「${entry.label}」？`} onConfirm={() => onDelete(entry)}>
            <Tooltip title="删除">
              <Button className="tc-icon-btn tc-icon-btn-danger" size="small" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ]

  if (error) return <Alert type="error" message={error} showIcon style={{ marginTop: 16 }} />

  const bulkActions =
    selectedIds.length > 0 ? (
      <Dropdown
        menu={{
          items: [
            { key: 'publish', label: '批量发布' },
            { key: 'unpublish', label: '批量取消发布' },
            { key: 'show', label: '批量显示' },
            { key: 'hide', label: '批量隐藏' },
          ],
          onClick: ({ key }) => runBulk(key as EntryBulkAction),
        }}
      >
        <Button>批量操作 ▾</Button>
      </Dropdown>
    ) : null

  return (
    <div className="tc-items-page">
      <div className="tc-page-header">
        <div>
          <h1 className="tc-page-title">条目列表</h1>
          <p className="tc-page-desc">查看和管理您的所有条目，支持搜索、筛选和批量操作</p>
        </div>
        <div className="tc-page-actions">
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            新建条目
          </Button>
          {bulkActions}
        </div>
      </div>

      <div className="tc-data-card">
        <div className="tc-toolbar tc-sticky-toolbar" ref={toolbarRef}>
          <Input
            placeholder="关键词（名称 / 编号 / 作者 / 年代）"
            prefix={<SearchOutlined />}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            allowClear
          />
          <Select
            placeholder="条目分类"
            allowClear
            style={{ width: 140 }}
            value={categoryId}
            onChange={(v) => {
              setCategoryId(v)
              setPage(1)
            }}
            options={categoryTerms.map((t) => ({ label: t.name, value: t.id }))}
          />
          <Select
            mode="multiple"
            placeholder="主题（多选）"
            allowClear
            style={{ minWidth: 160 }}
            value={topicIds}
            onChange={(v) => {
              setTopicIds(v)
              setPage(1)
            }}
            options={topicTerms.map((t) => ({ label: t.name, value: t.id }))}
          />
          <Select
            placeholder="发布状态"
            allowClear
            style={{ width: 110 }}
            value={status}
            onChange={(v) => {
              setStatus(v)
              setPage(1)
            }}
            options={[
              { label: '草稿', value: 'draft' },
              { label: '已发布', value: 'published' },
              { label: '已归档', value: 'archived' },
            ]}
          />
          <Select
            placeholder="前端展示"
            allowClear
            style={{ width: 110 }}
            value={visibility}
            onChange={(v) => {
              setVisibility(v)
              setPage(1)
            }}
            options={[
              { label: '展示', value: 'public' },
              { label: '隐藏', value: 'private' },
            ]}
          />
          <Select
            placeholder="图片处理"
            allowClear
            style={{ width: 110 }}
            value={imageStatus}
            onChange={(v) => {
              setImageStatus(v)
              setPage(1)
            }}
            options={Object.entries(IMAGE_STATUS_LABEL).map(([value, label]) => ({ label, value }))}
          />
          <Select
            style={{ width: 130 }}
            value={sort}
            onChange={setSort}
            options={[
              { label: '最近修改', value: 'updated_desc' },
              { label: '创建时间', value: 'created_desc' },
              { label: '名称', value: 'label_asc' },
              { label: '自定义顺序', value: 'sort_order' },
            ]}
          />
          <Button type="text" onClick={resetFilters}>
            重置
          </Button>
        </div>

        {selectedIds.length > 0 && (
          <div className="tc-bulk-bar">
            <Text>已选 {selectedIds.length} 项</Text>
            <Dropdown
              menu={{
                items: [
                  { key: 'publish', label: '批量发布' },
                  { key: 'unpublish', label: '批量取消发布' },
                  { key: 'show', label: '批量显示' },
                  { key: 'hide', label: '批量隐藏' },
                ],
                onClick: ({ key }) => runBulk(key as EntryBulkAction),
              }}
            >
              <Button size="small">批量操作 ▾</Button>
            </Dropdown>
            <Popconfirm title={`删除选中的 ${selectedIds.length} 项？`} onConfirm={() => runBulk('delete')}>
              <Button size="small" danger>
                批量删除
              </Button>
            </Popconfirm>
          </div>
        )}

        <Table
          className="tc-data-table"
          rowKey="id"
          loading={loading}
          dataSource={rows ?? []}
          columns={columns}
          scroll={{ x: 1424 }}
          sticky={{ offsetHeader: tableStickyOffset }}
          rowSelection={{
            selectedRowKeys: selectedIds,
            onChange: (keys) => setSelectedIds(keys as string[]),
          }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            pageSizeOptions: ['20', '50', '100'],
            showTotal: (t) => `共 ${t} 条目`,
            onChange: (p, ps) => {
              setPage(p)
              setPageSize(ps)
            },
          }}
        />
      </div>
    </div>
  )
}

function bulkActionLabel(action: EntryBulkAction): string {
  return { delete: '批量删除', publish: '批量发布', unpublish: '批量取消发布', show: '批量显示', hide: '批量隐藏', add_terms: '批量修改分类/主题' }[action]
}
